/**
 * AstroChart wheel bridge.
 *
 * TINKER WITH CHART LOOK HERE — edit CHART_SETTINGS (and the SIGN_FILL_* hex constants).
 * Hex colors (#RRGGBB) are fine everywhere a color is accepted.
 *
 * Not in this file:
 *   - Birth form labels → index.html
 *   - Positions / aspects tables → pipeline.js
 *   - Ephemeris math → celestine-adapter.js
 */

const NAME_TO_ASTRO = {
  Sun: "Sun",
  Moon: "Moon",
  Mercury: "Mercury",
  Venus: "Venus",
  Mars: "Mars",
  Jupiter: "Jupiter",
  Saturn: "Saturn",
  Uranus: "Uranus",
  Neptune: "Neptune",
  Pluto: "Pluto",
  Chiron: "Chiron",
  "North Node": "NNode",
  "South Node": "SNode",
  "True North Node": "NNode",
  "True South Node": "SNode",
  "Mean North Node": "NNode",
  "Mean South Node": "SNode",
};

// —— Element fills (edit these hex values) ——————————————————————————————
// Fire  = Aries, Leo, Sagittarius
// Earth = Taurus, Virgo, Capricorn
// Air   = Gemini, Libra, Aquarius
// Water = Cancer, Scorpio, Pisces
const SIGN_FILL_FIRE = "#FF8C00"; // bright orange (vivid warm orange)
const SIGN_FILL_EARTH = "#D4C4A8"; // light brown (warm sand / light earthy brown)
const SIGN_FILL_AIR = "#90D5FF"; // bright light blue (crisp bright sky blue)
const SIGN_FILL_WATER = "#2B7BBB"; // bright medium blue (vibrant medium ocean blue)

/**
 * AstroChart settings object.
 * Every key below is documented. Change values, save, hard-refresh (Ctrl+F5) on npm run dev.
 *
 * @type {Record<string, unknown>}
 */
const CHART_SETTINGS = {
  // —— Background & rings ————————————————————————————————————————————————
  /** Paper fill behind the whole wheel. */
  COLOR_BACKGROUND: "#fffcf7",
  /** Stroke color for the main chart circles. */
  CIRCLE_COLOR: "#44403c",
  /** Thickness of the main circles (higher = heavier). */
  CIRCLE_STRONG: 2,
  /** Color for general guide / connector lines. */
  LINE_COLOR: "#78716c",

  // —— Sign wedge fills (zodiac band) ————————————————————————————————————
  // Fire
  COLOR_ARIES: SIGN_FILL_FIRE,
  COLOR_LEO: SIGN_FILL_FIRE,
  COLOR_SAGITTARIUS: SIGN_FILL_FIRE,
  // Earth
  COLOR_TAURUS: SIGN_FILL_EARTH,
  COLOR_VIRGO: SIGN_FILL_EARTH,
  COLOR_CAPRICORN: SIGN_FILL_EARTH,
  // Air
  COLOR_GEMINI: SIGN_FILL_AIR,
  COLOR_LIBRA: SIGN_FILL_AIR,
  COLOR_AQUARIUS: SIGN_FILL_AIR,
  // Water
  COLOR_CANCER: SIGN_FILL_WATER,
  COLOR_SCORPIO: SIGN_FILL_WATER,
  COLOR_PISCES: SIGN_FILL_WATER,
  /**
   * Optional array of 12 fills in Aries→Pisces order.
   * Kept in sync with the COLOR_* keys above.
   */
  COLORS_SIGNS: [
    SIGN_FILL_FIRE, // Aries
    SIGN_FILL_EARTH, // Taurus
    SIGN_FILL_AIR, // Gemini
    SIGN_FILL_WATER, // Cancer
    SIGN_FILL_FIRE, // Leo
    SIGN_FILL_EARTH, // Virgo
    SIGN_FILL_AIR, // Libra
    SIGN_FILL_WATER, // Scorpio
    SIGN_FILL_FIRE, // Sagittarius
    SIGN_FILL_EARTH, // Capricorn
    SIGN_FILL_AIR, // Aquarius
    SIGN_FILL_WATER, // Pisces
  ],
  /**
   * true = no sign fills, outline-only wedges.
   * false = use the COLOR_* / COLORS_SIGNS fills above.
   */
  STROKE_ONLY: false,

  // —— Zodiac glyphs (Aries symbol, etc. in the sign band) ———————————————
  /** Color of the twelve sign symbols. */
  SIGNS_COLOR: "#1e3a5f",
  /** Stroke weight of sign symbols. */
  SIGNS_STROKE: 1.5,

  // —— Planets / points ————————————————————————————————————————————————
  /** Color of planet glyphs and their markers. */
  POINTS_COLOR: "#1c1917",
  /** Stroke weight of planet glyphs. */
  POINTS_STROKE: 2.2,
  /** Font size for degree text next to planets. */
  POINTS_TEXT_SIZE: 9,
  /**
   * How far stacked planets push apart when they occupy nearby longitudes.
   * Raise if glyphs overlap; lower if they drift too far from their ticks.
   */
  COLLISION_RADIUS: 14,
  /** Scale factor for all planetary / point symbols (1 = default). */
  SYMBOL_SCALE: 1.25,
  /**
   * true = show dignity abbreviations (r/d/e/f) next to planets.
   * false = cleaner wheel (recommended for this site).
   */
  SHOW_DIGNITIES_TEXT: false,

  // —— House cusps ——————————————————————————————————————————————————————
  /** Stroke weight of house cusp lines. */
  CUSPS_STROKE: 1,
  /** Color of house numbers (1–12). */
  CUSPS_FONT_COLOR: "#44403c",

  // —— Angles (ASC / DSC / MC / IC labels) ———————————————————————————————
  /** Color of axis angle labels (dark navy). Also reinforced via CSS in styles.css. */
  SYMBOL_AXIS_FONT_COLOR: "#0B2545",
  /** Stroke weight of axis angle marks. */
  SYMBOL_AXIS_STROKE: 1.6,

  // —— Layout ————————————————————————————————————————————————————————————
  /** Empty margin (px) between SVG edge and outer circle. */
  MARGIN: 50,
  /** Inner padding used when placing symbols. */
  PADDING: 18,
  /**
   * Rotates the entire chart in degrees.
   * Library default is often 180 so the Ascendant sits on the left (eastern horizon).
   */
  SHIFT_IN_DEGREES: 180,
  /**
   * Ratio controlling the inner hole / indoor circle size (larger → smaller hole).
   * Tweak carefully; values are library-specific.
   */
  INDOOR_CIRCLE_RADIUS_RATIO: 2,
  /** Ratio for the ring that separates signs from the aspect area. */
  INNER_CIRCLE_RADIUS_RATIO: 8,
  /** Depth of the degree-ruler ticks between rings. */
  RULER_RADIUS: 4,

  // —— Aspects drawn inside the wheel ————————————————————————————————————
  /**
   * Major aspects (conjunction, sextile, square, trine, opposition).
   * Each entry:
   *   degree — exact angle (0, 60, 90, 120, 180)
   *   orbit  — maximum orb in degrees (wider = more lines)
   *   color  — line color (hex). "transparent" hides the line.
   *
   * AstroChart computes aspects from planet positions itself.
   * Widen orbs for more lines; tighten for a sparser wheel.
   */
  // Traditional Red/Blue two-tone scheme: hard aspects (square, opposition) are
  // red; soft aspects (sextile, trine) are blue; conjunctions stay grey so they
  // remain visible but neutral. No green is used.
  ASPECTS: {
    /** 0° — grey so adjacent points on the perimeter stay visible. */
    conjunction: { degree: 0, orbit: 8, color: "#57534e" },
    /** 60° — soft / opportunity, blue. */
    sextile: { degree: 60, orbit: 4, color: "#2563eb" },
    /** 90° — tension, red. */
    square: { degree: 90, orbit: 6, color: "#dc2626" },
    /** 120° — flow, blue. */
    trine: { degree: 120, orbit: 6, color: "#2563eb" },
    /** 180° — tension, red. */
    opposition: { degree: 180, orbit: 7, color: "#dc2626" },
  },
};

export function toAstroData(model) {
  const planets = {};

  for (const body of model.bodies) {
    const key = NAME_TO_ASTRO[body.name];
    if (!key) continue;
    if (planets[key] && String(body.name).startsWith("Mean")) continue;
    planets[key] = [normalizeDeg(body.longitude)];
  }

  const sortedCusps = [...model.cusps].sort((a, b) => a.house - b.house);
  let cusps = sortedCusps.map((c) => normalizeDeg(c.longitude));

  if (cusps.length !== 12 && model.angles.length) {
    const asc = model.angles.find((a) => a.name === "Ascendant");
    if (asc) cusps = equalCuspsFromAsc(asc.longitude);
  }

  if (cusps.length !== 12) {
    throw new Error(`Expected 12 house cusps, got ${cusps.length}`);
  }

  return { planets, cusps };
}

/**
 * @astrodraw/astrochart ships a UMD bundle. Vite/ESM interop can nest the
 * constructor under default / default.Chart / astrochart.Chart.
 * @param {Record<string, unknown>} mod
 * @returns {new (elementId: string, width: number, height: number, settings?: object) => { radix: Function }}
 */
function resolveChartConstructor(mod) {
  const seen = new Set();
  const queue = [mod];

  while (queue.length) {
    const cur = queue.shift();
    if (cur == null || (typeof cur !== "object" && typeof cur !== "function")) continue;
    if (seen.has(cur)) continue;
    seen.add(cur);

    if (typeof cur === "function" && looksLikeChartCtor(cur)) {
      return cur;
    }

    for (const key of ["Chart", "default", "astrochart"]) {
      try {
        const next = cur[key];
        if (next != null) queue.push(next);
      } catch {
        // ignore getter errors
      }
    }
  }

  return null;
}

/** @param {Function} fn */
function looksLikeChartCtor(fn) {
  if (fn.prototype && typeof fn.prototype.radix === "function") return true;
  if (fn.name === "Chart" || fn.name === "m" || fn.name === "R") return true;
  try {
    return fn.length >= 2 && fn.length <= 4;
  } catch {
    return false;
  }
}

/**
 * Extract the AspectCalculator class from @astrodraw/astrochart (same nesting
 * strategy as resolveChartConstructor). It is required so we can pass our custom
 * CHART_SETTINGS.ASPECTS to the aspect calculation explicitly — the library's own
 * `radix.aspects()` method ignores the Chart's settings and falls back to a
 * hardcoded default (green/red, no sextile), which silently dropped our palette.
 * @param {Record<string, unknown>} mod
 * @returns {new (toPoints: object, settings?: object) => { radix: Function }}
 */
function resolveAspectCalculator(mod) {
  const seen = new Set();
  const queue = [mod];

  while (queue.length) {
    const cur = queue.shift();
    if (cur == null || (typeof cur !== "object" && typeof cur !== "function")) continue;
    if (seen.has(cur)) continue;
    seen.add(cur);

    if (
      typeof cur === "function" &&
      cur.prototype &&
      typeof cur.prototype.transit === "function" &&
      typeof cur.prototype.radix === "function"
    ) {
      // AspectCalculator (minified name varies) exposes both transit() and
      // radix() on its prototype; the Chart class exposes radix/scale/calibrate
      // but NOT transit, so this reliably distinguishes the two.
      return cur;
    }

    for (const key of ["AspectCalculator", "default", "astrochart"]) {
      try {
        const next = cur[key];
        if (next != null) queue.push(next);
      } catch {
        // ignore getter errors
      }
    }
  }

  return null;
}

export async function drawRadix(container, model, opts = {}) {
  if (!container) throw new Error("Chart container missing");

  const AstroChartModule = await import("@astrodraw/astrochart");
  const Chart = resolveChartConstructor(AstroChartModule);

  if (typeof Chart !== "function") {
    const keys = Object.keys(AstroChartModule || {}).join(", ") || "(none)";
    throw new Error(
      `AstroChart Chart constructor not found in package export (keys: ${keys})`
    );
  }

  // Wheel pixel size (not part of CHART_SETTINGS). The SVG canvas is drawn at a
  // fixed minimum virtual size (640px) so the planet-collision math in AstroChart
  // never fails on narrow phone screens; CSS then scales the finished SVG down
  // to fit (see .astro-chart svg in styles.css). Desktop rendering is unchanged.
  const width = opts.width || Math.max(640, Math.min(container.clientWidth || 640, 900));
  const height = opts.height || width;

  const hostId = "astro-chart-host";
  container.innerHTML = "";
  const host = document.createElement("div");
  host.id = hostId;
  container.appendChild(host);

  const data = toAstroData(model);

  const chart = new Chart(hostId, width, height, { ...CHART_SETTINGS });

  const radix = chart.radix(data);

  // Calculate aspects ourselves using CHART_SETTINGS.ASPECTS so the custom
  // Red/Blue palette (including sextiles) is honored. The library's radix.aspects()
  // would otherwise ignore these settings and use its hardcoded green/red defaults.
  if (radix && typeof radix.aspects === "function") {
    const AspectCalculator = resolveAspectCalculator(AstroChartModule);
    if (AspectCalculator && radix.toPoints) {
      const calc = new AspectCalculator(radix.toPoints, CHART_SETTINGS);
      const aspectList = calc.radix(data.planets);
      radix.aspects(aspectList);
    } else {
      radix.aspects();
    }
  }

  return chart;
}

function normalizeDeg(deg) {
  let d = deg % 360;
  if (d < 0) d += 360;
  return d;
}

function equalCuspsFromAsc(ascLon) {
  const cusps = [];
  for (let i = 0; i < 12; i++) {
    cusps.push(normalizeDeg(ascLon + i * 30));
  }
  return cusps;
}
